import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, CheckCircle } from "lucide-react";
import { useAssessment } from "@/context/AssessmentContext";
import { AssessmentOverlay } from "@/components/assessment/AssessmentOverlay";
import { useState, useEffect } from "react";
import { AssessmentModule } from "@/types/assessment";
import { useUserProfile } from "@/contexts/ProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
export function SimplifiedAssessmentDashboard() {
  const {
    modules,
    updateModuleStatus
  } = useAssessment();
  const {
    displayName
  } = useUserProfile();
  const { user } = useAuth();
  const [selectedModule, setSelectedModule] = useState<AssessmentModule | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Load completion status from database on mount
  useEffect(() => {
    async function loadCompletionStatus() {
      if (!user?.id) return;
      try {
        const { data, error } = await supabase
          .from('assessment_modules')
          .select('module_id, status')
          .eq('user_id', user.id);
        if (!error && data) {
          data.forEach(row => {
            if (row.status === 'complete') {
              updateModuleStatus(row.module_id, 'complete');
            } else if (row.status === 'in-progress') {
              updateModuleStatus(row.module_id, 'in-progress');
            }
          });
        }
      } catch (err) {
        console.error('Failed to load assessment completion status:', err);
      }
    }
    loadCompletionStatus();
  }, [user?.id]);
  
  const openAssessment = (module: AssessmentModule) => {
    setSelectedModule(module); // Open overlay for all modules
  };
  const toggleSection = (moduleId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    setExpandedSections(newExpanded);
  };
  const getOverallProgress = () => {
    const completedModules = modules.filter(m => m.status === 'complete').length;
    const inProgressModules = modules.filter(m => m.status === 'in-progress').length;
    return Math.round((completedModules + inProgressModules * 0.5) / modules.length * 100);
  };
  const getStepNumber = (index: number) => {
    return `STEP ${index + 1}`;
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'in-progress':
        return 'In Progress';
      default:
        return `${modules.find(m => m.status === 'not-started')?.estimatedTime || '5 minutes'}`;
    }
  };
  const primaryModule = modules.find(m => m.id === 'owner-readiness');
  const otherModules = modules.filter(m => m.id !== 'owner-readiness');
  return <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <h1 className="font-bold text-foreground text-left text-2xl">Welcome {displayName}</h1>
        
        
        <div className="my-4">
          <Button className="bg-accent hover:bg-accent/90 px-6 py-0 my-[20px]" onClick={() => {
            const nextIncomplete = modules.find(m => m.status !== 'complete');
            if (nextIncomplete) {
              openAssessment(nextIncomplete);
            } else {
              const planModule = modules.find(m => m.id === 'mysuccession-plan');
              if (planModule) openAssessment(planModule);
            }
          }}>Continue with Next Step →</Button>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Left Two Columns - Assessment Cards */}
        <div className="col-span-1 md:col-span-2 space-y-6">
          {/* Primary Module - Owner Readiness */}
          {primaryModule && <div className="space-y-4">
              <div className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded w-fit">
                {getStepNumber(0)}
              </div>
              
              <Card className={`border-l-4 border-l-primary rounded-l-none transition-all duration-200 ${primaryModule.status !== 'complete' ? 'cursor-pointer hover:shadow-md hover:bg-accent/50' : ''}`} onClick={primaryModule.status !== 'complete' ? () => openAssessment(primaryModule) : undefined}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{primaryModule.title}</CardTitle>
                        <Badge className={`ml-4 whitespace-nowrap ${primaryModule.status === 'complete' ? 'status-complete' : primaryModule.status === 'in-progress' ? 'status-in-progress' : 'status-pending'}`}>
                          {primaryModule.status === 'complete' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {primaryModule.status === 'complete' ? 'Complete' : primaryModule.status === 'in-progress' ? 'In Progress' : 'Not Started'}
                        </Badge>
                      </div>
                      <CardDescription className="text-xs text-muted-foreground">
                        Let's get started with a few initial questions to help us determine a baseline from which we will help you develop your succession plan.
                      </CardDescription>
                      <div className="text-xs text-muted-foreground">
                        Estimated time: {primaryModule.estimatedTime}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                
              </Card>
            </div>}

          {/* Additional Assessment Modules */}
          {otherModules.map((module, index) => {
            const ACTIVE_MODULES = [
              'owner-readiness-exit-timeline',
              'business-profile',
              'business-valuation',
              'business-optimization',
              'successor-strategy',
              'governance-family',
              'advisory-team',
              'financial-readiness',
              'exit-structure',
              'mysuccession-plan',
            ];
            const isActive = ACTIVE_MODULES.includes(module.id);
            return <div key={module.id} className="space-y-4">
              <div className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded w-fit">
                {getStepNumber(index + 1)}
              </div>
              
              <Card className={`border-l-4 border-l-muted rounded-l-none transition-all duration-200 ${isActive && module.status !== 'complete' ? 'cursor-pointer hover:shadow-md hover:bg-accent/50' : ''}`} onClick={isActive && module.status !== 'complete' ? () => openAssessment(module) : undefined}>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{module.title}</CardTitle>
                        {isActive ? <Badge className={`ml-4 whitespace-nowrap ${module.status === 'complete' ? 'status-complete' : module.status === 'in-progress' ? 'status-in-progress' : 'status-pending'}`}>
                            {module.status === 'complete' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {module.status === 'complete' ? 'Complete' : module.status === 'in-progress' ? 'In Progress' : 'Not Started'}
                          </Badge> : <Badge className={`ml-4 whitespace-nowrap status-pending`}>
                            Coming Soon
                          </Badge>}
                      </div>
                      <CardDescription className="text-xs text-muted-foreground">
                        {module.description}
                      </CardDescription>
                      <div className="text-xs text-muted-foreground">
                        Estimated time: {module.estimatedTime}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                
              </Card>
            </div>;
          })}

        </div>

        {/* Right Column - Progress & Notifications */}
        <div className="col-span-1 space-y-6">
          <div className="space-y-4">
            <div className="text-right space-y-2 -mt-3">
              <div className="text-sm text-muted-foreground font-bold">Overall progress: {getOverallProgress()}%</div>
              <Progress value={getOverallProgress()} className="w-full h-2" />
            </div>
            
            {/* TODO: Notifications — Phase 2 */}
          </div>
        </div>
      </div>

      <AssessmentOverlay module={selectedModule} onOpenChange={open => !open && setSelectedModule(null)} />
    </div>;
}